import { itemTypeName } from '@hive/shared';
import { Link } from 'react-router';
import { useGames } from '@/data/games';
import type { SearchHit } from '@/data/vault';
import { cn } from '@/lib/utils';
import { pathFor } from '@/vault/model';
import { groupHits, snippetParts } from './model';

export function Snippet({ text }: { text: string }) {
  return (
    <>
      {snippetParts(text).map((p, i) =>
        p.match ? (
          <mark key={i} className="bg-yellow/20 text-yellow">
            {p.text}
          </mark>
        ) : (
          <span key={i}>{p.text}</span>
        ),
      )}
    </>
  );
}

/** Search hits grouped by type, each linking to where it lives. `active` highlights one (the palette's cursor). */
export function SearchResults({
  hits,
  active,
  onOpen,
}: {
  hits: readonly SearchHit[];
  active?: string | null;
  onOpen?: () => void;
}) {
  const games = useGames();
  const gameList = games.data ?? [];
  const gameName = (id: string | null) => {
    const game = gameList.find((g) => g.id === id);
    return game && !game.is_studio ? game.name : null;
  };
  return (
    <div className="flex flex-col gap-4">
      {groupHits(hits).map((group) => (
        <section key={group.type} aria-label={group.plural} className="flex flex-col gap-1">
          <h2 className="font-display text-[9px] font-bold tracking-[0.18em] text-muted uppercase">
            {group.plural} <span className="text-dim">{group.hits.length}</span>
          </h2>
          <ul className="flex flex-col gap-px">
            {group.hits.map((h) => {
              const key = `${h.item_type}:${h.item_id}`;
              const game = gameName(h.game_id);
              return (
                <li key={key}>
                  <Link
                    to={pathFor(
                      { type: h.item_type, id: h.item_id, game_id: h.game_id, parent_id: h.parent_id },
                      gameList,
                    )}
                    onClick={onOpen}
                    data-active={active === key || undefined}
                    className={cn(
                      'flex flex-col gap-0.5 bg-bg-3/60 px-3 py-2 hover:bg-cyan/10',
                      active === key && 'bg-cyan/10 shadow-[inset_2px_0_0_var(--color-cyan)]',
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <span className="w-12 shrink-0 font-mono text-[10px] text-magenta uppercase">
                        {itemTypeName(h.item_type)}
                      </span>
                      <span className="flex-1 truncate text-sm font-semibold">{h.title}</span>
                      {game ? <span className="font-mono text-[10px] text-green">{game}</span> : null}
                      {h.status ? <span className="font-mono text-[10px] text-dim">{h.status}</span> : null}
                      {h.matched === 'meaning' ? (
                        <span
                          className="font-mono text-[10px] text-cyan"
                          title="No exact words in common, but about the same thing"
                        >
                          similar meaning
                        </span>
                      ) : null}
                    </span>
                    {h.snippet && h.snippet !== h.title ? (
                      <span className="line-clamp-2 pl-14 text-xs text-muted">
                        <Snippet text={h.snippet} />
                      </span>
                    ) : null}
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
